'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TestimonialCardProps {
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  testimonial: string;
  stayDuration: string;
  apartmentType: string;
  date: string;
}

export default function TestimonialCard({
  name,
  role,
  company,
  image,
  alt,
  rating,
  testimonial,
  stayDuration,
  apartmentType,
  date
}: TestimonialCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const shouldTruncate = testimonial.length > 200;
  const displayText = isExpanded || !shouldTruncate 
    ? testimonial 
    : `${testimonial.substring(0, 200)}...`;

  return (
    <div className="bg-white rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 p-6 border border-border">
      <div className="flex items-start space-x-4 mb-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-accent/20">
          <AppImage
            src={image}
            alt={alt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-semibold text-text-primary mb-1">
            {name}
          </h3>
          <p className="text-sm text-text-secondary mb-1">
            {role} at {company}
          </p>
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, index) => (
              <Icon
                key={index}
                name="StarIcon"
                variant={index < rating ? 'solid' : 'outline'}
                size={16}
                className={index < rating ? 'text-accent' : 'text-gray-300'}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-text-secondary leading-relaxed">
          "{displayText}"
        </p>
        {shouldTruncate && isHydrated && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-accent hover:text-accent/80 text-sm font-medium mt-2 transition-colors duration-300"
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="ClockIcon" size={16} className="text-accent" />
          <span>{stayDuration}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="HomeIcon" size={16} className="text-accent" />
          <span>{apartmentType}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="CalendarIcon" size={16} className="text-accent" />
          <span>{date}</span>
        </div>
      </div>
    </div>
  );
}